import type { BrandApp } from "@tento-ui/brand/brand-app/brand-app";
import type {
  NavDropdown as NavDropdownModel,
  SiteNav as SiteNavModel,
  SiteNavItem as SiteNavItemModel,
} from "@tento-ui/brand/@generated/models";
import { SiteNav } from "@tento-ui/brand/@generated/models";
import { LonaCardIcon } from "@tento-ui/components/icons/card-icon";
import { LonaChatIcon } from "@tento-ui/components/icons/chat-icon";
import { LonaLinkIcon } from "@tento-ui/components/icons/link-icon";
import { LonaSearchIcon } from "@tento-ui/components/icons/search-icon";
import { SheetsIcon } from "@tento-ui/components/icons/sheets-icon";
import { TasksIcon } from "@tento-ui/components/icons/tasks-icon";
import { LonaStickerIcon } from "@tento-ui/components/icons/sticker-icon";
import { LonaGlobeIcon } from "@tento-ui/components/icons/globe-icon";
import { LonaHeartIcon } from "@tento-ui/components/icons/heart-icon";
import { LonaChartIcon } from "@tento-ui/components/icons/chart-icon";
import { InstanceUrl } from "@tento-core/instance-url";
import { Router } from "@tento-ui/router";

import type {
  NavChild,
  NavDetailsColumn,
  NavDetailsConfig,
} from "@tento-ui/brand/blog/blog-design-system";

export type NavTargetMode = "current-host" | "root-host";

const MARKETING_PREFIXES = ["/for/", "/features/", "/solutions/", "/jobs/"];

function isMarketingHref(href: string): boolean {
  return MARKETING_PREFIXES.some((prefix) => (
    href === prefix || href.startsWith(prefix)
  ));
}

function navigate(href: string, targetMode: NavTargetMode): void {
  if (href.startsWith("http")) {
    window.open(href, "_blank");
  } else if (href === "/blog") {
    window.location.href = InstanceUrl.blog("/");
  } else if (targetMode === "root-host") {
    window.location.href = InstanceUrl.root(href);
  } else if (isMarketingHref(href)) {
    Router.navigateTo(href);
  } else {
    window.location.href = href;
  }
}

// Every mega-menu row carries a leading feature-icon tile. Map each nav target
// to its closest `std-*-icon`, keyed by `href` so items that point at the same
// surface (e.g. Stickers / "Mark the week") share one icon for free. `filled`
// is uniform — it forces the grayscale-first icons to their colored state and
// is a harmless no-op on the already-colored ones, so every tile reads colored.
const NAV_ICON_SIZE = 28;
const NAV_ICON_BY_HREF: Record<string, () => HTMLElement> = {
  "/features/calendar": () => LonaCardIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/features/stickers": () => LonaStickerIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/features/sheets": () => SheetsIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/features/integrations": () => LonaLinkIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/features/ai-chat": () => LonaChatIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/for/knowledge-workers": () => TasksIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/for/travelers": () => LonaGlobeIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/for/athletes": () => LonaHeartIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/solutions/timezone": () => LonaGlobeIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/solutions/health-fitness": () => LonaHeartIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/solutions/personal-analytics": () => LonaChartIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/solutions/team-scheduling": () => LonaCardIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/jobs/plan-the-week": () => TasksIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/jobs/travel-timezones": () => LonaGlobeIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/jobs/mark-the-week": () => LonaStickerIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/blog": () => LonaChatIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
  "/docs": () => LonaSearchIcon.makeWith({ variant: "filled", size: NAV_ICON_SIZE }),
};

function $iconForHref(href: string): Option<HTMLElement> {
  const make = NAV_ICON_BY_HREF[href];
  return make ? make() : null;
}

function navChildFromItem(item: {
  label: string;
  href: string;
  description?: Option<string>;
}): NavChild {
  return {
    label: item.label,
    href: item.href,
    description: item.description,
    $icon: $iconForHref(item.href),
  };
}

function navDetailsFromDropdown(dropdown: NavDropdownModel): NavDetailsConfig {
  const emptyColumn: NavDetailsColumn = {
    title: "",
    items: [],
    bold: undefined,
  };
  const mapped: NavDetailsColumn[] = dropdown.columns.map((col) => ({
    title: col.title,
    items: col.items.map((item) => navChildFromItem(item)),
    bold: col.bold ?? undefined,
  }));
  while (mapped.length < 3) {
    mapped.push(emptyColumn);
  }
  const footer = dropdown.footer;
  return {
    header: dropdown.header ?? null,
    columns: [mapped[0], mapped[1], mapped[2]],
    image: dropdown.image ?? undefined,
    footer: footer
      ? {
          label: footer.label,
          href: footer.href,
        }
      : null,
  };
}

export function navConfigFromSiteNav(
  nav: SiteNavModel,
  targetMode: NavTargetMode,
): BrandApp.NavConfig {
  const cta = nav.cta;
  return {
    items: nav.items.map((item: SiteNavItemModel) => ({
      label: item.label,
      href: item.href,
      details: item.details
        ? navDetailsFromDropdown(item.details)
        : undefined,
    })),
    cta: cta
      ? {
          label: cta.label,
          href: cta.href,
        }
      : null,
    // The single navigation strategy for the whole nav (targetMode-aware).
    onNavigate: (href: string) => navigate(href, targetMode),
  };
}

export function extractNavModel(): Option<SiteNavModel> {
  const $json = document.querySelector('script[data-model="nav"]');
  if (!$json?.textContent) return null;
  return SiteNav.parse(JSON.parse($json.textContent));
}
